"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { getCachedSchema } from "@/lib/ats-schema-cache"
import { expandNodeFields, isExpandableNode, mergeExpandedFields, nodesToExpandFor } from "@/lib/ats-schema-expansion"
import type { AtsExplorerFilter, AtsFieldDescriptor } from "@/types/ats-explorer-api"

export interface UseSchemaPathsOptions {
  /** Load as soon as the ATS system and entity type are known (default: wait for load()) */
  autoLoad?: boolean
  sampleSize?: number
  filters?: AtsExplorerFilter[]
  /** Paths that must be visible in the list; the association nodes on their way are expanded automatically */
  revealPaths?: readonly string[]
}

export interface UseSchemaPathsResult {
  /** Discovered fields, including any expansions; null until loaded */
  fields: AtsFieldDescriptor[] | null
  isLoading: boolean
  error: string | null
  /** Association nodes already expanded in place */
  expandedPaths: Set<string>
  /** The node currently being expanded, if any */
  expandingPath: string | null
  load: () => void
  /** Expands an association node by fetching the associated entity's schema (cached) */
  expand: (nodePath: string) => Promise<void>
}

/**
 * One entity's live schema with on-demand association expansion, shared by every
 * field picker. The schema itself is one call (session-cached); expanding a node
 * ("jobOrder") fetches the associated entity's schema (also cached — the same JobOrder
 * schema answers every picker) and re-roots its paths under the node. This is how
 * fields the parent metadata never advertises (Bullhorn "jobOrder.onSite") become
 * selectable without the server fanning out to every association.
 */
export function useSchemaPaths(entityType: string | null, options: UseSchemaPathsOptions = {}): UseSchemaPathsResult {
  const { autoLoad = false, sampleSize = 3, filters, revealPaths } = options
  const { atsSystem } = useTenantAtsSystem()
  const { getSchema } = useExplorer()

  const [fields, setFields] = useState<AtsFieldDescriptor[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set())
  const [expandingPath, setExpandingPath] = useState<string | null>(null)

  // Bumped on every entity change so a slow earlier entity's schema can't land on the current one
  const requestRef = useRef(0)
  // Nodes this hook has already expanded on its own, so a failure is not retried in a loop
  const autoExpandedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    requestRef.current++
    autoExpandedRef.current = new Set()
    setFields(null)
    setError(null)
    setExpandedPaths(new Set())
    setExpandingPath(null)
    setIsLoading(false)
  }, [entityType])

  const load = useCallback(() => {
    if (!atsSystem || !entityType) return
    const requestId = ++requestRef.current
    setIsLoading(true)
    setError(null)

    getCachedSchema(atsSystem, entityType, () => getSchema(atsSystem, entityType, sampleSize, filters ?? []), { sampleSize, filters })
      .then((schema) => {
        if (requestRef.current === requestId) setFields(schema.fields)
      })
      .catch((err) => {
        if (requestRef.current !== requestId) return
        setFields([])
        setError(err instanceof Error ? err.message : "Failed to discover fields")
      })
      .finally(() => {
        if (requestRef.current === requestId) setIsLoading(false)
      })
    // getSchema is a stable hook function; filters are passed by value per call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem, entityType, sampleSize])

  useEffect(() => {
    if (autoLoad && fields === null && !isLoading) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, atsSystem, entityType, fields])

  const expand = useCallback(
    async (nodePath: string) => {
      const node = fields?.find((field) => field.path === nodePath)
      if (!atsSystem || !node || !isExpandableNode(node) || expandedPaths.has(nodePath)) return

      const requestId = requestRef.current
      setExpandingPath(nodePath)
      try {
        const associated = node.associatedEntityType!
        const nested = await getCachedSchema(atsSystem, associated, () => getSchema(atsSystem, associated, sampleSize))
        if (requestRef.current !== requestId) return
        setFields((current) => mergeExpandedFields(current ?? [], nodePath, expandNodeFields(nodePath, nested.fields)))
        setExpandedPaths((current) => new Set(current).add(nodePath))
      } catch (err) {
        if (requestRef.current === requestId) setError(err instanceof Error ? err.message : `Failed to expand ${nodePath}`)
      } finally {
        if (requestRef.current === requestId) setExpandingPath(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [atsSystem, fields, expandedPaths, sampleSize]
  )

  // Selected paths under a collapsed node (remembered or carried in) would otherwise be invisible
  const revealKey = revealPaths?.join("|") ?? ""
  useEffect(() => {
    if (expandingPath || !revealPaths?.length) return
    const next = nodesToExpandFor(revealPaths, fields, expandedPaths).find((node) => !autoExpandedRef.current.has(node))
    if (!next) return
    autoExpandedRef.current.add(next)
    void expand(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey, fields, expandedPaths, expandingPath])

  return { fields, isLoading, error, expandedPaths, expandingPath, load, expand }
}
